import { Icon } from "@iconify/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { languageSwitcherOptions, LanguageCode } from "../../localization/supportedLanguages";
import { setLanguage } from "../../redux/features/language/languageSlice";
import { RootState } from "../../redux/store";
import { buildAppLink } from "../../utils/landingRedirect";

const bookingHref = "/internal/youwo-demo-booking";

const businessLogoMaskStyle: React.CSSProperties = {
  WebkitMask: 'url("/logo/logo.png") center / contain no-repeat',
  mask: 'url("/logo/logo.png") center / contain no-repeat',
};

const businessCopy = {
  en: {
    nav: {
      aria: "YouWo business landing navigation",
      home: "YouWo home",
      problem: "Problem",
      workflow: "Workflow",
      useCases: "Use cases",
      pricing: "Pricing",
      signIn: "Sign in",
      bookDemo: "Book Enterprise Max demo",
      languageLabel: "Switch language",
    },
    hero: {
      titleStart: "Your company should",
      titleAccent: "never",
      titleMiddle: "explain itself",
      titleGradient: "twice.",
      lead: "Enterprise Max connects the sources your team already uses, learns the repeatable workflow, and prepares source-grounded answers, drafts, reports, and follow-ups for approval.",
      secondaryCta: "See the workflow",
    },
    proofItems: [
      "High-volume workflows",
      "Connected source context",
      "Human approval gates",
      "Source-backed outputs",
    ],
    problem: {
      kicker: "The hidden cost",
      title: "Every handoff starts with reconstruction.",
      body: "The customer update is in email. The policy is in a file. The meeting context is in a recording. Before anyone can act, the team has to piece the story back together and remember how the work is supposed to happen.",
      cards: [
        {
          label: "Before",
          title: "Scattered sources",
          body: "Important context is trapped in the last email, meeting note, PDF, or private thread.",
        },
        {
          label: "With YouWo",
          title: "One context layer",
          body: "Facts, owners, decisions, source links, and workflow preferences stay ready for people and agents.",
        },
        {
          label: "After",
          title: "Action with approval",
          body: "Briefs, replies, reports, and tasks get prepared automatically before anything is sent.",
        },
      ],
    },
    workflow: {
      kicker: "Source to action",
      title: "Teach YouWo the workflow once. Review the work every time.",
      body: "Start with one repeatable business moment. YouWo collects the context, shows where it came from, learns the pattern, prepares the work, and waits for the right human approval.",
      capture: "1. Capture",
      understand: "2. Understand",
      execute: "3. Execute",
      memoryTitle: "Customer rollout context",
      memoryItems: [
        "Decision: pilot scope approved for 2 departments.",
        "Blocker: legal needs final data retention language.",
        "Owner: Maya to confirm deployment date by Friday.",
      ],
      citations: ["Gmail", "Transcript", "PDF"],
    },
    sourceStream: [
      { icon: "lucide:mail", label: "Client email", detail: "Maya asked for pilot terms" },
      { icon: "lucide:audio-lines", label: "Meeting", detail: "3 commitments, 2 blockers" },
      { icon: "lucide:file-text", label: "Policy PDF", detail: "Legal approved clause 4.2" },
      { icon: "lucide:calendar-days", label: "Calendar", detail: "Budget review at 2 PM" },
    ],
    actionQueue: [
      { title: "Draft the follow-up", meta: "Uses email + meeting transcript", status: "Needs approval" },
      { title: "Prepare investor brief", meta: "Pulls metrics, risks, and owners", status: "Ready" },
      { title: "Create owner tasks", meta: "Routes to the right team", status: "Queued" },
    ],
    useCases: {
      kicker: "Where teams use it",
      title: "Use Enterprise Max where context gets lost between tools.",
      body: "Start with the moments where someone has to search across email, calls, files, calendars, and chat before they can answer or act.",
      items: [
        {
          label: "Sales",
          title: "Every handoff starts with the current customer story.",
          body: "YouWo gathers emails, calls, docs, and decisions into a sourced brief, then drafts the next follow-up.",
          icon: "lucide:handshake",
        },
        {
          label: "Operations",
          title: "Recurring work stops living in someone's head.",
          body: "Weekly reporting, status checks, approvals, and owner reminders become repeatable workflows.",
          icon: "lucide:route",
        },
        {
          label: "Leadership",
          title: "Walk into meetings already caught up.",
          body: "Ask what changed, what is blocked, and who owes what before the meeting begins.",
          icon: "lucide:compass",
        },
      ],
    },
    pricing: {
      kicker: "Pricing",
      title: "Enterprise Max is scoped with you.",
      body: "Every company connects different sources and runs different workflows. Book a demo and we will scope the right sources, workflow volume, approval model, and rollout plan.",
      discussLabel: "What we discuss",
      discussTitle: "Your workflows, sources, approvals, volume, and rollout plan.",
      bullets: [
        "Connected tools and data sources",
        "Workflow volume, credit needs, and team size",
        "Approval, security, and deployment needs",
      ],
    },
    demo: {
      aria: "Animated YouWo workflow preview",
      contextLabel: "Context layer",
      question: "What changed since yesterday?",
      answerItems: ["3 decisions", "2 blockers", "4 next steps"],
      approvalLabel: "Waiting for approval",
      approvalAction: "Send sourced follow-up to Maya",
      approve: "Approve",
    },
  },
  zh: {
    nav: {
      aria: "YouWo 企业落地页导航",
      home: "YouWo 首页",
      problem: "问题",
      workflow: "工作流",
      useCases: "使用场景",
      pricing: "价格",
      signIn: "登录",
      bookDemo: "预约 Enterprise Max 演示",
      languageLabel: "切换语言",
    },
    hero: {
      titleStart: "你的公司不该",
      titleAccent: "一再",
      titleMiddle: "重新解释",
      titleGradient: "自己",
      lead: "Enterprise Max 会连接团队已经使用的信息来源，学习可重复的工作流，并在审批前准备带来源的回答、草稿、报告和 follow-up",
      secondaryCta: "查看工作流",
    },
    proofItems: [
      "高频工作流",
      "连接来源上下文",
      "人工审批节点",
      "带来源输出",
    ],
    problem: {
      kicker: "隐藏成本",
      title: "每一次交接，都从重新拼上下文开始",
      body: "客户更新在邮件里，政策在文件里，会议背景在录音里，任何人要开始行动前，都得先把这段故事重新拼一遍，并记住这件事应该怎么做",
      cards: [
        {
          label: "之前",
          title: "信息散落各处",
          body: "关键上下文被困在上一封邮件、会议记录、PDF 或私人聊天里",
        },
        {
          label: "使用 YouWo",
          title: "统一上下文层",
          body: "事实、负责人、决策、来源链接和工作流偏好随时准备好，给人和 Agent 使用",
        },
        {
          label: "之后",
          title: "审批后再行动",
          body: "简报、回复、报告和任务会先自动准备好，发送前等待人工确认",
        },
      ],
    },
    workflow: {
      kicker: "从来源到行动",
      title: "教 YouWo 一次工作流，每次只需要 review 结果",
      body: "从一个可重复的业务场景开始，YouWo 收集上下文，展示来源，学习模式，准备好执行内容，并等待正确的人审批",
      capture: "1. 捕捉",
      understand: "2. 理解",
      execute: "3. 执行",
      memoryTitle: "客户上线上下文",
      memoryItems: [
        "决策：两个部门的试点范围已确认",
        "阻塞：法务需要确认最终的数据保留条款",
        "负责人：Maya 周五前确认部署日期",
      ],
      citations: ["Gmail", "会议转写", "PDF"],
    },
    sourceStream: [
      { icon: "lucide:mail", label: "客户邮件", detail: "Maya 询问试点条款" },
      { icon: "lucide:audio-lines", label: "会议", detail: "3 个承诺，2 个阻塞" },
      { icon: "lucide:file-text", label: "政策 PDF", detail: "法务批准第 4.2 条" },
      { icon: "lucide:calendar-days", label: "日历", detail: "下午 2 点预算评审" },
    ],
    actionQueue: [
      { title: "起草跟进邮件", meta: "使用邮件和会议转写", status: "等待审批" },
      { title: "准备投资人简报", meta: "提取指标、风险和负责人", status: "已准备" },
      { title: "创建负责人任务", meta: "路由给正确团队", status: "排队中" },
    ],
    useCases: {
      kicker: "团队如何使用",
      title: "在上下文容易丢失的地方使用 Enterprise Max",
      body: "从那些需要跨邮件、电话、文件、日历和聊天搜索，才能回答或行动的时刻开始",
      items: [
        {
          label: "销售",
          title: "每次交接都从最新客户故事开始",
          body: "YouWo 把邮件、通话、文档和决策整理成带来源的简报，然后起草下一封跟进",
          icon: "lucide:handshake",
        },
        {
          label: "运营",
          title: "重复工作不再只存在某个人脑子里",
          body: "周报、状态检查、审批和负责人提醒会变成可重复的工作流",
          icon: "lucide:route",
        },
        {
          label: "管理层",
          title: "进入会议前就已经跟上最新进展",
          body: "会议开始前就能知道发生了什么、哪里卡住、谁还欠什么",
          icon: "lucide:compass",
        },
      ],
    },
    pricing: {
      kicker: "价格",
      title: "Enterprise Max 会和你一起定制",
      body: "每家公司连接的来源和运行的工作流都不同。预约演示后，我们会一起确认来源、工作流量、审批方式和上线计划",
      discussLabel: "我们会讨论",
      discussTitle: "你的工作流、信息来源、审批方式、使用量和上线计划",
      bullets: [
        "需要连接的工具和数据来源",
        "工作流数量、credits 需求和团队规模",
        "审批、安全和部署需求",
      ],
    },
    demo: {
      aria: "YouWo 工作流动画预览",
      contextLabel: "上下文层",
      question: "从昨天到现在发生了什么？",
      answerItems: ["3 个决策", "2 个阻塞", "4 个下一步"],
      approvalLabel: "等待审批",
      approvalAction: "向 Maya 发送带来源的跟进",
      approve: "批准",
    },
  },
  ja: {
    nav: {
      aria: "YouWo ビジネス向けランディングページのナビゲーション",
      home: "YouWo ホーム",
      problem: "課題",
      workflow: "ワークフロー",
      useCases: "活用例",
      pricing: "料金",
      signIn: "ログイン",
      bookDemo: "Enterprise Max デモを予約",
      languageLabel: "言語を切り替え",
    },
    hero: {
      titleStart: "会社は",
      titleAccent: "何度も",
      titleMiddle: "自分を説明し直す必要は",
      titleGradient: "ありません",
      lead: "Enterprise Max はチームがすでに使う情報源を接続し、繰り返しワークフローを学習し、承認前に根拠付き回答、下書き、レポート、フォローアップを準備します",
      secondaryCta: "ワークフローを見る",
    },
    proofItems: [
      "高ボリュームワークフロー",
      "接続されたソース文脈",
      "人による承認ゲート",
      "根拠付きアウトプット",
    ],
    problem: {
      kicker: "見えないコスト",
      title: "引き継ぎはいつも、文脈の再構築から始まります",
      body: "顧客の更新はメールに、ポリシーはファイルに、会議の背景は録音にあります。動き出す前に、チームは毎回ストーリーと作業手順を組み立て直さなければなりません",
      cards: [
        {
          label: "Before",
          title: "散らばった情報源",
          body: "重要な文脈は、最後のメール、会議メモ、PDF、プライベートスレッドに閉じ込められています",
        },
        {
          label: "With YouWo",
          title: "ひとつのコンテキスト層",
          body: "事実、担当者、決定事項、出典リンク、ワークフローの好みを、人と Agent がすぐ使える状態にします",
        },
        {
          label: "After",
          title: "承認してから実行",
          body: "ブリーフ、返信、レポート、タスクを自動で準備し、送信前に人が確認できます",
        },
      ],
    },
    workflow: {
      kicker: "情報源から実行へ",
      title: "ワークフローを一度教え、毎回アウトプットを確認する",
      body: "まずは繰り返し発生する業務シーンから、YouWo が文脈を集め、出典を示し、パターンを学び、作業を準備し、適切な承認を待ちます",
      capture: "1. 取り込む",
      understand: "2. 理解する",
      execute: "3. 実行する",
      memoryTitle: "顧客導入の文脈",
      memoryItems: [
        "決定：2 部門でのパイロット範囲が承認済み",
        "ブロッカー：法務がデータ保持文言の最終確認中",
        "担当：Maya が金曜日までに導入日を確認",
      ],
      citations: ["Gmail", "文字起こし", "PDF"],
    },
    sourceStream: [
      { icon: "lucide:mail", label: "顧客メール", detail: "Maya がパイロット条件を確認" },
      { icon: "lucide:audio-lines", label: "会議", detail: "3 件のコミット、2 件のブロッカー" },
      { icon: "lucide:file-text", label: "ポリシー PDF", detail: "法務が 4.2 条を承認" },
      { icon: "lucide:calendar-days", label: "カレンダー", detail: "午後 2 時に予算レビュー" },
    ],
    actionQueue: [
      { title: "フォローアップを下書き", meta: "メールと会議文字起こしを使用", status: "承認待ち" },
      { title: "投資家向けブリーフを準備", meta: "指標、リスク、担当者を抽出", status: "準備完了" },
      { title: "担当者タスクを作成", meta: "適切なチームへルーティング", status: "キュー済み" },
    ],
    useCases: {
      kicker: "チームでの使い方",
      title: "ツールの間で文脈が失われる場所に Enterprise Max を",
      body: "メール、通話、ファイル、カレンダー、チャットを探し回らないと回答や実行ができない瞬間から始めます",
      items: [
        {
          label: "営業",
          title: "引き継ぎは最新の顧客ストーリーから",
          body: "YouWo がメール、通話、ドキュメント、決定事項を出典付きブリーフにまとめ、次のフォローアップを下書きします",
          icon: "lucide:handshake",
        },
        {
          label: "オペレーション",
          title: "繰り返し業務を誰かの頭の中だけに置かない",
          body: "週次レポート、ステータス確認、承認、担当者リマインドを再利用できるワークフローにします",
          icon: "lucide:route",
        },
        {
          label: "リーダーシップ",
          title: "会議に入る前から最新状況を把握",
          body: "何が変わり、何が止まり、誰が何を持っているのかを会議前に確認できます",
          icon: "lucide:compass",
        },
      ],
    },
    pricing: {
      kicker: "料金",
      title: "Enterprise Max は一緒にスコープします",
      body: "接続するソースやワークフローは会社ごとに異なります。デモでソース、ワークフロー量、承認モデル、導入計画を一緒に確認します",
      discussLabel: "確認する内容",
      discussTitle: "ワークフロー、情報源、承認フロー、使用量、導入計画",
      bullets: [
        "接続するツールとデータソース",
        "ワークフロー量、credits の必要量、チーム規模",
        "承認、セキュリティ、導入要件",
      ],
    },
    demo: {
      aria: "YouWo ワークフローのアニメーションプレビュー",
      contextLabel: "コンテキスト層",
      question: "昨日から何が変わった？",
      answerItems: ["3 件の決定", "2 件のブロッカー", "4 件の次アクション"],
      approvalLabel: "承認待ち",
      approvalAction: "Maya に根拠付きフォローアップを送信",
      approve: "承認",
    },
  },
} satisfies Record<LanguageCode, {
  nav: Record<string, string>;
  hero: Record<string, string>;
  proofItems: string[];
  problem: {
    kicker: string;
    title: string;
    body: string;
    cards: Array<{ label: string; title: string; body: string }>;
  };
  workflow: {
    kicker: string;
    title: string;
    body: string;
    capture: string;
    understand: string;
    execute: string;
    memoryTitle: string;
    memoryItems: string[];
    citations: string[];
  };
  sourceStream: Array<{ icon: string; label: string; detail: string }>;
  actionQueue: Array<{ title: string; meta: string; status: string }>;
  useCases: {
    kicker: string;
    title: string;
    body: string;
    items: Array<{ label: string; title: string; body: string; icon: string }>;
  };
  pricing: {
    kicker: string;
    title: string;
    body: string;
    discussLabel: string;
    discussTitle: string;
    bullets: string[];
  };
  demo: {
    aria: string;
    contextLabel: string;
    question: string;
    answerItems: string[];
    approvalLabel: string;
    approvalAction: string;
    approve: string;
  };
}>;

type BusinessCopy = typeof businessCopy.en;

const YouWoBusinessLanding: React.FC = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const [languageOpen, setLanguageOpen] = React.useState(false);
  const copy = businessCopy[currentLanguage] ?? businessCopy.en;
  const currentLanguageLabel = languageSwitcherOptions.find((option) => option.code === currentLanguage)?.shortLabel ?? "EN";

  const handleLanguageChange = (language: LanguageCode) => {
    dispatch(setLanguage(language));
    i18n.changeLanguage(language);
    setLanguageOpen(false);
  };

  return (
    <main className="yw-business-landing" data-lang={currentLanguage}>
      <style>{businessLandingStyles}</style>

      <nav className="yw-nav" aria-label={copy.nav.aria}>
        <a className="yw-logo" href="#top" aria-label={copy.nav.home}>
          <span aria-hidden="true" className="yw-logo-mark" style={businessLogoMaskStyle} />
          <span className="yw-logo-text">YouWo</span>
        </a>
        <div className="yw-nav-links">
          <a href="#problem">{copy.nav.problem}</a>
          <a href="#workflow">{copy.nav.workflow}</a>
          <a href="#use-cases">{copy.nav.useCases}</a>
          <a href="#pricing">{copy.nav.pricing}</a>
        </div>
        <div className="yw-nav-actions">
          <a className="yw-quiet-link" href={buildAppLink()}>{copy.nav.signIn}</a>
          <div className="yw-language-menu">
            <button
              aria-expanded={languageOpen}
              aria-label={copy.nav.languageLabel}
              className="yw-language-trigger"
              onClick={() => setLanguageOpen((isOpen) => !isOpen)}
              type="button"
            >
              {currentLanguageLabel}
              <Icon icon="lucide:chevron-down" />
            </button>
            {languageOpen ? (
              <div className="yw-language-popover">
                {languageSwitcherOptions.map((option) => (
                  <button
                    className={option.code === currentLanguage ? "yw-language-option yw-language-option-active" : "yw-language-option"}
                    key={option.code}
                    onClick={() => handleLanguageChange(option.code)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <a className="yw-button yw-button-dark" href={bookingHref}>{copy.nav.bookDemo}</a>
        </div>
      </nav>

      <section className="yw-section yw-hero" id="top">
        <div className="yw-hero-bg" aria-hidden="true">
          <div className="yw-sky-layer" />
          <div className="yw-hill yw-hill-back" />
          <div className="yw-hill yw-hill-mid" />
          <div className="yw-hill yw-hill-front" />
        </div>

        <div className="yw-hero-copy">
          <h1>
            {copy.hero.titleStart} <span className="yw-title-accent">{copy.hero.titleAccent}</span> {copy.hero.titleMiddle} <span className="yw-title-gradient">{copy.hero.titleGradient}</span>
          </h1>
          <p className="yw-lead">
            {copy.hero.lead}
          </p>
          <div className="yw-hero-actions">
            <a className="yw-button yw-button-dark" href={bookingHref}>{copy.nav.bookDemo}</a>
            <a className="yw-button yw-button-light" href="#workflow">{copy.hero.secondaryCta}</a>
          </div>
          <div className="yw-proof-row" aria-label="YouWo proof points">
            {copy.proofItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>

        <FlowDemo demo={copy.demo} sourceStream={copy.sourceStream} />
      </section>

      <section className="yw-section yw-problem" id="problem">
        <div className="yw-section-copy">
          <p className="yw-kicker">{copy.problem.kicker}</p>
          <h2>{copy.problem.title}</h2>
          <p>
            {copy.problem.body}
          </p>
        </div>
        <div className="yw-reset-grid">
          {copy.problem.cards.map((card, index) => (
            <div className={index === 1 ? "yw-reset-card yw-reset-card-accent" : "yw-reset-card"} key={card.label}>
              <span>{card.label}</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="yw-section yw-workflow" id="workflow">
        <div className="yw-section-copy">
          <p className="yw-kicker">{copy.workflow.kicker}</p>
          <h2>{copy.workflow.title}</h2>
          <p>
            {copy.workflow.body}
          </p>
        </div>
        <div className="yw-workflow-stage">
          <div className="yw-workflow-column">
            <p className="yw-column-label">{copy.workflow.capture}</p>
            {copy.sourceStream.map((source, index) => (
              <div className="yw-source-card" style={{ animationDelay: `${index * 160}ms` }} key={source.label}>
                <Icon icon={source.icon} />
                <div>
                  <strong>{source.label}</strong>
                  <span>{source.detail}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="yw-memory-panel">
            <p className="yw-column-label">{copy.workflow.understand}</p>
            <h3>{copy.workflow.memoryTitle}</h3>
            <ul>
              {copy.workflow.memoryItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="yw-citation-row">
              {copy.workflow.citations.map((citation) => (
                <span key={citation}>{citation}</span>
              ))}
            </div>
          </div>
          <div className="yw-workflow-column">
            <p className="yw-column-label">{copy.workflow.execute}</p>
            {copy.actionQueue.map((action, index) => (
              <div className="yw-action-card" style={{ animationDelay: `${400 + index * 160}ms` }} key={action.title}>
                <div>
                  <strong>{action.title}</strong>
                  <span>{action.meta}</span>
                </div>
                <small>{action.status}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="yw-section yw-use-cases" id="use-cases">
        <div className="yw-section-copy">
          <p className="yw-kicker">{copy.useCases.kicker}</p>
          <h2>{copy.useCases.title}</h2>
          <p>
            {copy.useCases.body}
          </p>
        </div>
        <div className="yw-use-grid">
          {copy.useCases.items.map((useCase) => (
            <article className="yw-use-card" key={useCase.label}>
              <span className="yw-use-icon">
                <Icon icon={useCase.icon} />
              </span>
              <p>{useCase.label}</p>
              <h3>{useCase.title}</h3>
              <span>{useCase.body}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="yw-section yw-pricing" id="pricing">
        <div className="yw-section-copy">
          <p className="yw-kicker">{copy.pricing.kicker}</p>
          <h2>{copy.pricing.title}</h2>
          <p>
            {copy.pricing.body}
          </p>
        </div>
        <div className="yw-pricing-contact">
          <div>
            <p>{copy.pricing.discussLabel}</p>
            <h3>{copy.pricing.discussTitle}</h3>
          </div>
          <ul>
            {copy.pricing.bullets.map((bullet) => (
              <li key={bullet}><Icon icon="lucide:check" />{bullet}</li>
            ))}
          </ul>
          <a className="yw-button yw-button-dark" href={bookingHref}>{copy.nav.bookDemo}</a>
        </div>
      </section>
    </main>
  );
};

const FlowDemo: React.FC<{ demo: BusinessCopy["demo"]; sourceStream: BusinessCopy["sourceStream"] }> = ({ demo, sourceStream }) => (
  <div className="yw-flow-demo" aria-label={demo.aria}>
    <div className="yw-demo-window">
      <div className="yw-demo-bar">
        <span />
        <span />
        <span />
      </div>
      <div className="yw-demo-content">
        <div className="yw-demo-sources">
          {sourceStream.slice(0, 3).map((source, index) => (
            <div className="yw-floating-source" style={{ animationDelay: `${index * 0.75}s` }} key={source.label}>
              <Icon icon={source.icon} />
              <span>{source.label}</span>
            </div>
          ))}
        </div>
        <div className="yw-demo-core">
          <p>{demo.contextLabel}</p>
          <h3>{demo.question}</h3>
          <div className="yw-demo-answer">
            {demo.answerItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
        <div className="yw-demo-approval">
          <span>{demo.approvalLabel}</span>
          <strong>{demo.approvalAction}</strong>
          <button type="button">{demo.approve}</button>
        </div>
      </div>
    </div>
  </div>
);

const businessLandingStyles = `
@import url("https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap");

.yw-business-landing {
  --ink: #1d261f;
  --muted: #687066;
  --line: rgba(45, 58, 48, 0.14);
  --paper: #fffdf7;
  --sage: #dbe8d3;
  --moss: #78906c;
  --logo-color: #172019;
  --mint: #dff2ea;
  --peach: #f7d9c9;
  --butter: #f7edc7;
  --lavender: #ebe7fb;
  --sky: #dbeefa;
  --clay: #bd806c;
  --display-font: "Playfair Display", Georgia, serif;
  --accent-font: var(--display-font);
  --display-weight: 400;
  --accent-weight: var(--display-weight);
  --hero-title-size: clamp(54px, 5.45vw, 80px);
  --hero-title-line-height: 1.08;
  --hero-title-max-width: 920px;
  --demo-title-size: clamp(42px, 3.85vw, 70px);
  --demo-offset: clamp(108px, 9vw, 150px);
  background: var(--paper);
  color: var(--ink);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  overflow-x: hidden;
  scroll-snap-type: none;
}

.yw-business-landing * {
  box-sizing: border-box;
}

#webpack-dev-server-client-overlay {
  display: none !important;
  pointer-events: none !important;
}

.yw-business-landing a {
  color: inherit;
  text-decoration: none;
}

.yw-nav {
  align-items: center;
  background: rgba(255, 253, 247, 0.82);
  border: 1px solid var(--line);
  border-radius: 999px;
  display: grid;
  gap: 18px;
  grid-template-columns: auto 1fr auto;
  left: 50%;
  max-width: min(1180px, calc(100vw - 40px));
  padding: 10px 12px;
  position: fixed;
  top: 18px;
  transform: translateX(-50%);
  width: 100%;
  z-index: 20;
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
  box-shadow: 0 14px 45px rgba(74, 83, 68, 0.08);
}

.yw-logo,
.yw-nav-actions,
.yw-nav-links,
.yw-proof-row,
.yw-hero-actions,
.yw-citation-row {
  align-items: center;
  display: flex;
}

.yw-logo {
  gap: 10px;
  font-size: 17px;
  font-weight: 760;
  padding-left: 8px;
}

.yw-logo-mark {
  background: var(--logo-color);
  display: inline-flex;
  height: 31px;
  width: 31px;
}

.yw-logo-text {
  color: var(--logo-color);
  font-family: var(--display-font);
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0;
}

.yw-nav-links {
  gap: 28px;
  justify-content: center;
  color: var(--muted);
  font-size: 14px;
  font-weight: 650;
}

.yw-nav-actions {
  gap: 10px;
  justify-content: flex-end;
}

.yw-quiet-link {
  color: var(--muted);
  font-size: 14px;
  font-weight: 650;
  padding: 11px 12px;
}

.yw-language-menu {
  position: relative;
}

.yw-language-trigger {
  align-items: center;
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 999px;
  color: #65725f;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 13px;
  font-weight: 780;
  gap: 6px;
  min-height: 40px;
  padding: 0 13px;
  white-space: nowrap;
}

.yw-language-trigger svg {
  font-size: 14px;
}

.yw-language-popover {
  background: rgba(255, 253, 247, 0.96);
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 18px;
  box-shadow: 0 18px 44px rgba(74, 83, 68, 0.14);
  display: grid;
  gap: 3px;
  min-width: 132px;
  padding: 7px;
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  z-index: 30;
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.yw-language-option {
  background: transparent;
  border: 0;
  border-radius: 12px;
  color: #65725f;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 780;
  min-height: 34px;
  padding: 0 10px;
  text-align: left;
  transition: background-color 180ms ease, color 180ms ease;
  white-space: nowrap;
  width: 100%;
}

.yw-language-option-active {
  background: #172019;
  color: #fffdf7;
}

.yw-button {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 15px;
  font-weight: 760;
  justify-content: center;
  min-height: 46px;
  padding: 0 22px;
  transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease;
  white-space: nowrap;
}

.yw-button:hover {
  transform: translateY(-1px);
}

.yw-business-landing .yw-button-dark {
  background: #172019;
  color: #fffdf7;
  box-shadow: 0 14px 30px rgba(28, 36, 28, 0.18);
}

.yw-business-landing .yw-button-light {
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid var(--line);
  color: var(--ink);
}

.yw-section {
  min-height: 100vh;
  padding: 120px max(28px, calc((100vw - 1180px) / 2)) 84px;
  position: relative;
  scroll-margin-top: 96px;
  scroll-snap-align: none;
}

.yw-hero {
  align-items: center;
  display: flex;
  flex-direction: column;
  isolation: isolate;
  overflow: hidden;
  padding-left: max(28px, calc((100vw - 1320px) / 2));
  padding-right: max(28px, calc((100vw - 1320px) / 2));
  padding-top: 132px;
}

.yw-hero-bg {
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: -1;
}

.yw-sky-layer {
  background:
    linear-gradient(180deg, rgba(219, 238, 250, 0.7), rgba(255, 253, 247, 0) 58%),
    linear-gradient(135deg, rgba(235, 231, 251, 0.5), rgba(247, 217, 201, 0.36) 46%, rgba(219, 232, 211, 0.42));
  inset: 0;
  position: absolute;
}

.yw-hill {
  bottom: -2px;
  height: 38vh;
  left: 0;
  position: absolute;
  right: 0;
}

.yw-hill-back {
  background: var(--sky);
  clip-path: polygon(0 48%, 11% 38%, 24% 52%, 39% 29%, 54% 46%, 70% 27%, 86% 44%, 100% 31%, 100% 100%, 0 100%);
  opacity: 0.72;
}

.yw-hill-mid {
  background: var(--sage);
  clip-path: polygon(0 62%, 14% 45%, 31% 58%, 47% 38%, 62% 52%, 78% 35%, 100% 55%, 100% 100%, 0 100%);
  opacity: 0.82;
}

.yw-hill-front {
  background: linear-gradient(180deg, #eaf0df, #fffdf7 94%);
  clip-path: polygon(0 72%, 17% 56%, 37% 70%, 55% 49%, 78% 69%, 100% 54%, 100% 100%, 0 100%);
}

.yw-hero-copy {
  max-width: 1100px;
  text-align: center;
  width: 100%;
}

.yw-kicker {
  align-items: center;
  color: #65725f;
  display: inline-flex;
  gap: 8px;
  font-size: 14px;
  font-weight: 760;
  letter-spacing: 0;
  margin: 0;
}

.yw-hero h1,
.yw-section-copy h2 {
  font-family: var(--display-font);
  font-weight: var(--display-weight);
  letter-spacing: 0;
  margin: 0;
}

.yw-hero h1 {
  font-size: var(--hero-title-size);
  line-height: var(--hero-title-line-height);
  margin-top: 16px;
  max-width: var(--hero-title-max-width);
  margin-left: auto;
  margin-right: auto;
}

.yw-business-landing[data-lang="zh"] .yw-hero h1,
.yw-business-landing[data-lang="ja"] .yw-hero h1 {
  font-size: clamp(48px, 5.05vw, 74px);
  line-height: 1.12;
  max-width: 980px;
}

.yw-title-accent {
  color: #6f8f6b;
  display: inline-block;
  font-family: var(--accent-font);
  font-weight: var(--accent-weight);
}

.yw-title-gradient {
  background: linear-gradient(100deg, #7d9670 0%, #c88473 42%, #6b9eb4 72%, #7d9670 100%);
  background-size: 260% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
  font-family: var(--accent-font);
  font-weight: var(--accent-weight);
}

.yw-lead {
  color: #4f584d;
  font-size: clamp(18px, 1.65vw, 23px);
  line-height: 1.55;
  margin: 44px auto 0;
  max-width: 820px;
}

.yw-hero-actions {
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 34px;
}

.yw-proof-row {
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 34px;
}

.yw-proof-row span {
  background: rgba(255, 255, 255, 0.54);
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 999px;
  color: #606a5e;
  font-size: 13px;
  font-weight: 720;
  padding: 9px 13px;
}

.yw-flow-demo {
  align-items: center;
  align-self: center;
  display: flex;
  margin-top: var(--demo-offset);
  max-width: min(1220px, calc(100vw - 56px));
  min-height: auto;
  position: relative;
  width: 100%;
}

.yw-demo-window {
  background: rgba(255, 253, 247, 0.68);
  border: 1px solid rgba(45, 58, 48, 0.14);
  border-radius: 26px;
  box-shadow: 0 36px 90px rgba(79, 93, 77, 0.16);
  margin: 0 auto;
  max-width: 1220px;
  overflow: hidden;
  position: relative;
  top: auto;
  width: 100%;
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.yw-demo-bar {
  align-items: center;
  border-bottom: 1px solid rgba(45, 58, 48, 0.1);
  display: flex;
  gap: 7px;
  height: 58px;
  padding: 0 22px;
}

.yw-demo-bar span {
  background: #d3cbbc;
  border-radius: 999px;
  display: block;
  height: 10px;
  width: 10px;
}

.yw-demo-content {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(210px, 0.78fr) minmax(360px, 1.2fr) minmax(260px, 0.9fr);
  min-height: 560px;
  padding: 30px;
}

.yw-demo-sources {
  display: grid;
  gap: 18px;
  grid-template-rows: repeat(3, 1fr);
}

.yw-floating-source {
  align-items: center;
  animation: ywFloatCard 6s ease-in-out infinite;
  background: rgba(255, 255, 255, 0.64);
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 18px;
  color: #475245;
  display: flex;
  font-size: 16px;
  font-weight: 760;
  gap: 12px;
  padding: 22px;
}

.yw-floating-source:nth-child(1) { background: rgba(247, 217, 201, 0.56); }
.yw-floating-source:nth-child(2) { background: rgba(223, 242, 234, 0.6); }
.yw-floating-source:nth-child(3) { background: rgba(235, 231, 251, 0.6); }

.yw-demo-core {
  align-self: stretch;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.82), rgba(247, 237, 199, 0.42));
  border: 1px solid rgba(45, 58, 48, 0.13);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 500px;
  padding: 38px;
}

.yw-demo-core p,
.yw-demo-approval span,
.yw-column-label {
  color: #65725f;
  font-size: 12px;
  font-weight: 820;
  letter-spacing: 0.12em;
  margin: 0;
  text-transform: uppercase;
}

.yw-demo-core h3 {
  font-family: var(--display-font);
  font-size: var(--demo-title-size);
  font-weight: var(--display-weight);
  line-height: 0.96;
  margin: 18px 0 0;
}

.yw-demo-answer {
  display: grid;
  gap: 9px;
  margin-top: 32px;
}

.yw-demo-answer span {
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(45, 58, 48, 0.1);
  border-radius: 999px;
  color: #475245;
  font-size: 14px;
  font-weight: 760;
  padding: 10px 13px;
}

.yw-demo-approval {
  align-self: stretch;
  background: #172019;
  border-radius: 22px;
  color: #fffdf7;
  display: grid;
  gap: 13px;
  grid-template-rows: auto 1fr auto;
  padding: 22px;
}

.yw-demo-approval span {
  color: rgba(255, 253, 247, 0.58);
}

.yw-demo-approval strong {
  align-self: center;
  font-size: 22px;
  line-height: 1.25;
}

.yw-demo-approval button {
  background: #e6f0d8;
  border: 0;
  border-radius: 999px;
  color: #172019;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 820;
  height: 42px;
  justify-self: start;
  padding: 0 18px;
}

.yw-section-copy {
  max-width: 760px;
}

.yw-section-copy h2 {
  font-size: clamp(48px, 6.5vw, 92px);
  line-height: 0.96;
  margin-top: 18px;
}

.yw-section-copy p:not(.yw-kicker) {
  color: #5d665a;
  font-size: clamp(18px, 1.35vw, 22px);
  line-height: 1.6;
  margin: 24px 0 0;
}

.yw-problem {
  align-items: center;
  background: #fffdf7;
  display: grid;
  gap: 48px;
}

.yw-reset-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.yw-reset-card {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid var(--line);
  border-radius: 24px;
  min-height: 300px;
  padding: 26px;
}

.yw-reset-card:nth-child(1) { background: rgba(219, 238, 250, 0.56); }
.yw-reset-card:nth-child(3) { background: rgba(235, 231, 251, 0.52); }

.yw-reset-card-accent {
  background: #172019;
  color: #fffdf7;
}

.yw-reset-card span,
.yw-use-card p {
  color: #7a866f;
  display: block;
  font-size: 12px;
  font-weight: 840;
  letter-spacing: 0.12em;
  margin: 0;
  text-transform: uppercase;
}

.yw-reset-card-accent span,
.yw-reset-card-accent p {
  color: rgba(255, 253, 247, 0.66);
}

.yw-reset-card h3 {
  font-family: var(--display-font);
  font-size: 42px;
  font-weight: var(--display-weight);
  line-height: 1;
  margin: 76px 0 0;
}

.yw-reset-card p {
  color: #5d665a;
  font-size: 17px;
  line-height: 1.55;
  margin: 18px 0 0;
}

.yw-workflow {
  background:
    linear-gradient(180deg, rgba(219, 232, 211, 0.26), rgba(255, 253, 247, 0.94)),
    #fffdf7;
}

.yw-workflow-stage {
  align-items: stretch;
  display: grid;
  gap: 18px;
  grid-template-columns: 0.82fr 1.04fr 0.82fr;
  margin-top: 48px;
}

.yw-workflow-column,
.yw-memory-panel {
  border: 1px solid rgba(45, 58, 48, 0.14);
  border-radius: 26px;
  padding: 22px;
}

.yw-workflow-column {
  background: rgba(255, 255, 255, 0.58);
  display: grid;
  gap: 14px;
}

.yw-source-card,
.yw-action-card {
  animation: ywRiseIn 720ms ease both;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 18px;
  display: flex;
  gap: 14px;
  padding: 16px;
}

.yw-source-card svg {
  color: var(--moss);
  flex: 0 0 auto;
  font-size: 22px;
  margin-top: 2px;
}

.yw-source-card div,
.yw-action-card div {
  display: grid;
  gap: 5px;
}

.yw-source-card strong,
.yw-action-card strong {
  font-size: 15px;
}

.yw-source-card span,
.yw-action-card span {
  color: #687066;
  font-size: 13px;
  line-height: 1.35;
}

.yw-memory-panel {
  background: #172019;
  color: #fffdf7;
  min-height: 460px;
}

.yw-memory-panel .yw-column-label {
  color: rgba(255, 253, 247, 0.56);
}

.yw-memory-panel h3 {
  font-family: var(--display-font);
  font-size: 48px;
  font-weight: var(--display-weight);
  line-height: 1;
  margin: 28px 0 24px;
}

.yw-memory-panel ul {
  display: grid;
  gap: 18px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.yw-memory-panel li {
  border-top: 1px solid rgba(255, 253, 247, 0.12);
  color: rgba(255, 253, 247, 0.82);
  font-size: 16px;
  line-height: 1.5;
  padding-top: 17px;
}

.yw-citation-row {
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 36px;
}

.yw-citation-row span {
  background: rgba(255, 253, 247, 0.1);
  border: 1px solid rgba(255, 253, 247, 0.14);
  border-radius: 999px;
  color: rgba(255, 253, 247, 0.72);
  font-size: 13px;
  font-weight: 760;
  padding: 9px 12px;
}

.yw-action-card {
  align-items: flex-start;
  justify-content: space-between;
}

.yw-action-card small {
  background: var(--butter);
  border-radius: 999px;
  color: #5b542e;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 840;
  padding: 7px 9px;
}

.yw-use-cases {
  align-content: center;
  background:
    linear-gradient(180deg, rgba(255, 253, 247, 0.8), rgba(223, 242, 234, 0.34)),
    #fffdf7;
  display: grid;
  gap: 48px;
}

.yw-use-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.yw-use-card {
  background: rgba(255, 255, 255, 0.66);
  border: 1px solid var(--line);
  border-radius: 26px;
  min-height: 380px;
  padding: 26px;
}

.yw-use-card:nth-child(1) { background: rgba(247, 217, 201, 0.42); }
.yw-use-card:nth-child(2) { background: rgba(219, 238, 250, 0.44); }
.yw-use-card:nth-child(3) { background: rgba(235, 231, 251, 0.44); }

.yw-use-icon {
  align-items: center;
  background: rgba(255, 253, 247, 0.66);
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 18px;
  color: #2f3a30;
  display: inline-flex;
  font-size: 22px;
  height: 52px;
  justify-content: center;
  margin-bottom: 74px;
  width: 52px;
}

.yw-use-card h3 {
  font-family: var(--display-font);
  font-size: 42px;
  font-weight: var(--display-weight);
  line-height: 1;
  margin: 18px 0 20px;
}

.yw-use-card > span:last-child {
  color: #5d665a;
  display: block;
  font-size: 16px;
  line-height: 1.55;
}

.yw-pricing {
  align-content: center;
  background:
    linear-gradient(180deg, rgba(255, 253, 247, 0.88), rgba(235, 231, 251, 0.24)),
    #fffdf7;
  display: grid;
  gap: 44px;
}

.yw-pricing-contact {
  align-items: center;
  background:
    linear-gradient(140deg, rgba(255, 253, 247, 0.08), rgba(219, 232, 211, 0.06)),
    #172019;
  border: 1px solid rgba(45, 58, 48, 0.14);
  border-radius: 32px;
  color: #fffdf7;
  display: grid;
  gap: 32px;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 0.72fr) auto;
  padding: clamp(28px, 4vw, 54px);
}

.yw-pricing-contact p {
  color: rgba(255, 253, 247, 0.58);
  font-size: 12px;
  font-weight: 840;
  letter-spacing: 0.12em;
  margin: 0;
  text-transform: uppercase;
}

.yw-pricing-contact h3 {
  font-family: var(--display-font);
  font-size: clamp(36px, 4.2vw, 62px);
  font-weight: var(--display-weight);
  line-height: 1;
  margin: 18px 0 0;
  max-width: 620px;
}

.yw-pricing-contact ul {
  display: grid;
  gap: 14px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.yw-pricing-contact li {
  align-items: center;
  color: rgba(255, 253, 247, 0.76);
  display: flex;
  font-size: 15px;
  font-weight: 720;
  gap: 10px;
  line-height: 1.35;
}

.yw-pricing-contact li svg {
  color: #a9c69a;
  flex: 0 0 auto;
}

.yw-pricing-contact .yw-button {
  background: #fffdf7;
  color: #172019;
  justify-self: end;
}

@keyframes ywFloatCard {
  0%, 100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(9px, -8px, 0);
  }
}

@keyframes ywRiseIn {
  0% {
    opacity: 0;
    transform: translateY(14px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: no-preference) {
  .yw-hero-copy {
    animation: ywHeroCopyIn 900ms cubic-bezier(0.19, 1, 0.22, 1) both;
  }

  .yw-demo-window {
    animation: ywDemoArrive 1100ms 180ms cubic-bezier(0.19, 1, 0.22, 1) both;
  }

  .yw-proof-row span,
  .yw-hero-actions .yw-button {
    animation: ywSoftRise 720ms cubic-bezier(0.19, 1, 0.22, 1) both;
  }

  .yw-hero-actions .yw-button:nth-child(1) { animation-delay: 420ms; }
  .yw-hero-actions .yw-button:nth-child(2) { animation-delay: 500ms; }
  .yw-proof-row span:nth-child(1) { animation-delay: 600ms; }
  .yw-proof-row span:nth-child(2) { animation-delay: 660ms; }
  .yw-proof-row span:nth-child(3) { animation-delay: 720ms; }
  .yw-proof-row span:nth-child(4) { animation-delay: 780ms; }

  .yw-hill-back {
    animation: ywHillBreathe 12s ease-in-out infinite alternate;
  }

  .yw-hill-mid {
    animation: ywHillBreathe 14s 500ms ease-in-out infinite alternate-reverse;
  }

  .yw-title-accent {
    animation: ywWordTint 1100ms 260ms cubic-bezier(0.19, 1, 0.22, 1) both;
  }

  .yw-title-gradient {
    animation:
      ywWordTint 1100ms 360ms cubic-bezier(0.19, 1, 0.22, 1) both,
      ywTextGradient 7s 1.1s ease-in-out infinite alternate;
  }
}

@keyframes ywHeroCopyIn {
  0% {
    opacity: 0;
    transform: translateY(24px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes ywDemoArrive {
  0% {
    opacity: 0;
    transform: translate3d(0, 44px, 0) scale(0.97);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
}

@keyframes ywSoftRise {
  0% {
    opacity: 0;
    transform: translateY(12px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes ywHillBreathe {
  0% {
    transform: translateX(-1.2%) translateY(0);
  }
  100% {
    transform: translateX(1.2%) translateY(8px);
  }
}

@keyframes ywWordTint {
  0% {
    opacity: 0;
    transform: translateY(0.18em);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes ywTextGradient {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
}

@media (max-width: 1120px) {
  .yw-flow-demo {
    min-height: auto;
  }

  .yw-demo-window {
    max-width: none;
  }
}

@media (max-width: 1020px) {
  .yw-nav {
    grid-template-columns: auto auto;
  }

  .yw-nav-links {
    display: none;
  }

  .yw-pricing-contact,
  .yw-workflow-stage {
    grid-template-columns: 1fr;
  }

  .yw-demo-content {
    grid-template-columns: 1fr;
  }

  .yw-demo-core,
  .yw-demo-approval {
    grid-column: auto;
    grid-row: auto;
  }

  .yw-reset-grid,
  .yw-use-grid {
    grid-template-columns: 1fr;
  }

  .yw-reset-card,
  .yw-use-card {
    min-height: auto;
  }

  .yw-pricing-contact .yw-button {
    justify-self: start;
  }

  .yw-reset-card h3,
  .yw-use-icon {
    margin-top: 48px;
  }

  .yw-use-icon {
    margin-bottom: 28px;
  }
}

@media (max-width: 640px) {
  .yw-business-landing {
    scroll-snap-type: none;
  }

  .yw-nav {
    border-radius: 24px;
    gap: 10px;
    grid-template-columns: auto auto;
    justify-content: space-between;
    max-width: calc(100vw - 24px);
    padding: 10px 12px;
    top: 10px;
  }

  .yw-logo {
    font-size: 15px;
    min-width: 0;
  }

  .yw-quiet-link {
    display: none;
  }

  .yw-nav-actions {
    gap: 8px;
    min-width: 0;
  }

  .yw-nav .yw-button {
    display: none;
  }

  .yw-language-trigger {
    font-size: 12px;
    min-height: 36px;
    padding: 0 10px;
  }

  .yw-language-option {
    font-size: 11px;
    min-height: 30px;
    padding: 0 7px;
  }

  .yw-section {
    min-height: auto;
    padding: 110px 18px 64px;
  }

  .yw-hero h1 {
    font-size: clamp(55px, 16vw, 76px);
  }

  .yw-business-landing[data-lang="zh"] .yw-hero h1,
  .yw-business-landing[data-lang="ja"] .yw-hero h1 {
    font-size: clamp(44px, 13.5vw, 62px);
    line-height: 1.12;
  }

  .yw-section-copy h2 {
    font-size: 44px;
  }

  .yw-demo-content,
  .yw-workflow-column,
  .yw-memory-panel,
  .yw-pricing-contact {
    padding: 16px;
  }

  .yw-memory-panel h3,
  .yw-use-card h3,
  .yw-reset-card h3 {
    font-size: 36px;
  }
}
`;

export default YouWoBusinessLanding;
